// hooks/useCurrentUser.ts - Enhanced with mock session support
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

export interface CurrentUser {
  employeeId: string | null | undefined;
  id: string;
  name: string;
  email: string;
  shortName: string;
  role?: string; // Added role for mock session
  employee: {
    rcRegionCode?: string;
    empLastName?: string;
    empFirstName?: string;
    empAuthorization?: string | null;
    empAuthorization2?: string | null;
    empAeId?: string;
    empFullName?: string;
    roleName?: string;
  };
}

export const useCurrentUser = (): CurrentUser => {
  const { data: realSession } = useSession();

  // Check if we should use mock session
  const shouldBypass = process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_BYPASS_AUTH === 'true';

  // Create mock session data
  const mockSession = useMemo(
    () => ({
      user: {
        id: 'dev-1',
        name: 'Dev User',
        email: 'dev@example.com',
        role: 'admin',
        employeeId: 'E001',
        employee: {
          rcRegionCode: 'INM',
          empLastName: 'Dev',
          empFirstName: 'User',
          empAuthorization: 'auth1',
          empAuthorization2: 'auth2',
          empAeId: '001',
          empFullName: 'User Dev',
        },
      },
    }),
    [],
  );

  // Use mock session when bypassing, otherwise use real session
  const session = shouldBypass ? mockSession : realSession;

  return useMemo(
    () => ({
      id: session?.user?.id ?? '',
      name: session?.user?.name ?? '',
      email: session?.user?.email ?? '',
      employeeId: session?.user?.employeeId ?? '',
      role: session?.user?.role ?? '',
      employee: {
        rcRegionCode: session?.user?.employee?.rcRegionCode ?? '',
        empLastName: session?.user?.employee?.empLastName ?? '',
        empFirstName: session?.user?.employee?.empFirstName ?? '',
        empAuthorization: session?.user?.employee?.empAuthorization ?? '',
        empAuthorization2: session?.user?.employee?.empAuthorization2 ?? '',
        empAeId: session?.user?.employee?.empAeId ?? '',
        empFullName: session?.user?.employee?.empFullName ?? '',
      },
      shortName: session?.user?.name ? session.user.name.substring(0, 2).toUpperCase() : '',
    }),
    [
      session?.user?.id,
      session?.user?.name,
      session?.user?.email,
      session?.user?.employeeId,
      session?.user?.role,
      session?.user?.employee,
    ],
  );
};
